import { getHTMLFromFragment, getSchema, type JSONContent } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { memo, useLayoutEffect, useMemo, useRef } from "react";
import { useStore } from "zustand";
import { useIsSharedWorkspace } from "@/hooks/use-shared-workspace";
import { coreStore, type UserEntry } from "@/lib/stores/core-store";
import {
	getTiptapReadOnlyExtensions,
	tiptapRendererStyles,
} from "@/lib/tiptap-config";
import { cn } from "@/lib/utils";

type RichTextRendererProps = {
	richContent: Record<string, unknown>;
	className?: string;
};

// Message content is read-only, so it does not need a live editor to display.
// Each rendered message used to spin up its own read-only TipTap `useEditor`, a
// full ProseMirror EditorView with schema, plugin state, transaction pipeline,
// DOM and listeners. In a busy channel that meant 100+ live editors in the
// viewport, and they were never torn down as rows scrolled away, so they
// accumulated over the tab's lifetime, saturating the main thread and thrashing
// GC until the tab hung. See TECH-3041 (builds on TECH-3010).
//
// Instead this serializes the ProseMirror JSON to static HTML with the SAME
// read-only extension set the editor used, so the markup, and its safety
// properties of schema-known nodes and marks only with text escaped, match what
// the read-only EditorView rendered. No EditorView is created, so there is
// nothing to retain and nothing to re-reconcile per frame.
//
// Two costs are deliberately hoisted out of the hot path so scrolling stays
// smooth, because virtualization mounts a fresh row every time one scrolls into
// view:
//   1. The schema is built ONCE at module load. `getSchema()` resolves all
//      extensions and is the expensive part of TipTap's `generateHTML()`, which
//      rebuilds it on every call. Doing that per row made scrolling janky.
//   2. Generated HTML is cached per message content in a WeakMap keyed on the
//      stable `richContent` reference, so a row scrolled out and back in reuses
//      the cached string instead of re-serializing. Entries are collected when
//      the message leaves the store.
const renderExtensions = getTiptapReadOnlyExtensions();
const renderSchema = getSchema(renderExtensions);
const htmlCache = new WeakMap<object, string>();

function renderToHtml(richContent: Record<string, unknown>): string | null {
	const cached = htmlCache.get(richContent);
	if (cached !== undefined) {
		return cached;
	}
	try {
		const node = ProseMirrorNode.fromJSON(
			renderSchema,
			richContent as JSONContent
		);
		const html = getHTMLFromFragment(node.content, renderSchema);
		htmlCache.set(richContent, html);
		return html;
	} catch {
		return null;
	}
}

function tagMentions(container: HTMLElement, users: Map<string, UserEntry>) {
	for (const mention of container.querySelectorAll<HTMLElement>(
		".mention[data-id]"
	)) {
		const organizationName = users.get(mention.dataset.id ?? "")?.profile
			.organization?.name;
		if (organizationName) {
			mention.dataset.organization = organizationName;
		} else {
			delete mention.dataset.organization;
		}
	}
}

function clearMentionTags(container: HTMLElement) {
	for (const mention of container.querySelectorAll<HTMLElement>(
		".mention[data-organization]"
	)) {
		delete mention.dataset.organization;
	}
}

function RichTextRendererImpl({
	richContent,
	className,
}: RichTextRendererProps) {
	const html = useMemo(() => renderToHtml(richContent), [richContent]);
	const containerRef = useRef<HTMLDivElement>(null);
	const shared = useIsSharedWorkspace();
	const users = useStore(coreStore, (state) => state.users);

	const markup = useMemo(() => ({ __html: html ?? "" }), [html]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!(container && markup.__html)) return;
		if (shared) {
			tagMentions(container, users);
		} else {
			clearMentionTags(container);
		}
	}, [shared, users, markup]);

	if (html === null) {
		return (
			<p className={cn(tiptapRendererStyles, className)}>
				Unable to render message
			</p>
		);
	}

	// TipTap's schema serializer produces `html` from stored ProseMirror JSON. Only
	// schema-known nodes and marks are emitted, text content is escaped, and the
	// Link mark blanks disallowed hrefs such as javascript: at serialize time. This
	// is the exact DOM the read-only EditorView rendered before, and no raw user
	// HTML is interpolated. See TECH-3041.
	return (
		<div
			className={cn(tiptapRendererStyles, className)}
			// biome-ignore lint/security/noDangerouslySetInnerHtml: serialized from a constrained TipTap schema, not raw user HTML, see the comment above
			dangerouslySetInnerHTML={markup}
			ref={containerRef}
		/>
	);
}

export const RichTextRenderer = memo(RichTextRendererImpl);
